// src/pages/Sessions.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { db } from '../lib/firebase';
import { collection, query, orderBy, limit, getDocs, startAfter } from 'firebase/firestore';
import { ChevronDown, ChevronRight, Users, Receipt, Clock } from 'lucide-react';

export default function Sessions() {
  const { merchant } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const PAGE_SIZE = 20;

  const loadSessions = useCallback(async (after = null) => {
    if (!merchant?.id) return;
    setLoading(true);
    try {
      let q = query(
        collection(db, 'merchants', merchant.id, 'sessions'),
        orderBy('createdAt', 'desc'),
        limit(PAGE_SIZE)
      );
      if (after) q = query(
        collection(db, 'merchants', merchant.id, 'sessions'),
        orderBy('createdAt', 'desc'),
        startAfter(after),
        limit(PAGE_SIZE)
      );
      const snap = await getDocs(q);
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setSessions(prev => after ? [...prev, ...docs] : docs);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [merchant?.id]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const statusColor = (s) => {
    if (s === 'completed') return { bg: '#f0fdf4', color: '#16a34a' };
    if (s === 'splitting') return { bg: '#fefce8', color: '#ca8a04' };
    return { bg: '#f5f5f5', color: '#888' };
  };

  return (
    <div>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '32px', fontWeight: 500, color: '#1a1a1a', marginBottom: '6px' }}>
          Session History
        </h1>
        <p style={{ fontSize: '14px', color: '#888' }}>Every time a group split a check at your restaurant.</p>
      </div>

      <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '40px 1fr 100px 120px 120px 90px',
          gap: '12px', padding: '14px 20px',
          background: '#fafafa', borderBottom: '1px solid #f0f0f0',
        }}>
          {['', 'Table', 'Party', 'Check Total', 'Date', 'Status'].map(h => (
            <div key={h} style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#aaa' }}>{h}</div>
          ))}
        </div>

        {sessions.length === 0 && !loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#bbb' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>📋</div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#888', marginBottom: '6px' }}>No sessions yet</div>
            <div style={{ fontSize: '13px' }}>Sessions appear here after guests split checks at your tables.</div>
          </div>
        ) : (
          sessions.map(s => {
            const sc = statusColor(s.status);
            const isOpen = expanded === s.id;
            const date = s.createdAt?.toDate?.();
            return (
              <div key={s.id} style={{ borderBottom: '1px solid #fafafa' }}>
                <div
                  onClick={() => setExpanded(isOpen ? null : s.id)}
                  style={{
                    display: 'grid', gridTemplateColumns: '40px 1fr 100px 120px 120px 90px',
                    gap: '12px', padding: '14px 20px',
                    cursor: 'pointer', transition: 'background 0.15s',
                    background: isOpen ? 'rgba(64,0,228,0.03)' : 'transparent',
                    alignItems: 'center',
                  }}
                  onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = '#fafafa'; }}
                  onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb' }}>
                    {isOpen ? <ChevronDown size={14} color="#4000E4" /> : <ChevronRight size={14} />}
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#1a1a1a' }}>{s.tableLabel || s.tableId || '—'}</div>
                  <div style={{ fontSize: '13px', color: '#555' }}>{s.partySize ? `${s.partySize} people` : '—'}</div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#1a1a1a' }}>{s.totalAmount ? `$${s.totalAmount.toFixed(2)}` : '—'}</div>
                  <div style={{ fontSize: '12px', color: '#888' }}>
                    {date ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' · ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '—'}
                  </div>
                  <div>
                    <span style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '3px 10px', borderRadius: '20px', background: sc.bg, color: sc.color }}>
                      {s.status || 'open'}
                    </span>
                  </div>
                </div>

                {isOpen && (
                  <div style={{ padding: '0 20px 20px 72px', background: 'rgba(64,0,228,0.02)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>

                      {s.receipt?.items?.length > 0 && (
                        <div style={{ background: '#fff', borderRadius: '12px', padding: '16px', border: '1px solid rgba(0,0,0,0.06)' }}>
                          <div style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#aaa', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Receipt size={12} /> Receipt Items
                          </div>
                          {s.receipt.items.map((item, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '4px 0', borderBottom: i < s.receipt.items.length - 1 ? '1px solid #fafafa' : 'none' }}>
                              <span style={{ color: '#555' }}>{item.name}</span>
                              <span style={{ fontWeight: 600, color: '#1a1a1a' }}>${item.price?.toFixed(2)}</span>
                            </div>
                          ))}
                          {s.receipt.tax && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '8px 0 4px', borderTop: '1px solid #eee', color: '#888' }}>
                              <span>Tax</span><span>${s.receipt.tax?.toFixed(2)}</span>
                            </div>
                          )}
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '4px 0', fontWeight: 700, color: '#1a1a1a' }}>
                            <span>Total</span><span>${s.receipt.total?.toFixed(2) || s.totalAmount?.toFixed(2)}</span>
                          </div>
                        </div>
                      )}

                      {s.splits?.length > 0 && (
                        <div style={{ background: '#fff', borderRadius: '12px', padding: '16px', border: '1px solid rgba(0,0,0,0.06)' }}>
                          <div style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#aaa', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Users size={12} /> Splits
                          </div>
                          {s.splits.map((split, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', padding: '6px 0', borderBottom: i < s.splits.length - 1 ? '1px solid #fafafa' : 'none' }}>
                              <span style={{ color: '#555' }}>{split.displayName || split.userId || 'Guest'}</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontWeight: 600, color: '#1a1a1a' }}>${split.amount?.toFixed(2)}</span>
                                <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '20px', background: split.paid ? '#f0fdf4' : '#fef9c3', color: split.paid ? '#16a34a' : '#ca8a04', fontWeight: 600 }}>
                                  {split.paid ? 'paid' : 'pending'}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      <div style={{ background: '#fff', borderRadius: '12px', padding: '16px', border: '1px solid rgba(0,0,0,0.06)' }}>
                        <div style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#aaa', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Clock size={12} /> Details
                        </div>
                        {[
                          ['Session ID', s.id.slice(0, 12) + '...'],
                          ['Source', s.receiptSource || 'ocr'],
                          ['Started', date?.toLocaleString() || '—'],
                        ].map(([k, v]) => (
                          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '4px 0', color: '#888' }}>
                            <span>{k}</span><span style={{ color: '#555', fontWeight: 500 }}>{v}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: '24px', color: '#aaa', fontSize: '13px' }}>Loading...</div>
        )}

        {hasMore && !loading && sessions.length > 0 && (
          <div style={{ padding: '16px', textAlign: 'center' }}>
            <button
              onClick={() => loadSessions(lastDoc)}
              style={{
                padding: '10px 28px', background: 'transparent', color: '#4000E4',
                border: '1px solid rgba(64,0,228,0.3)', borderRadius: '10px',
                fontFamily: "'Outfit', sans-serif", fontSize: '13px', fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Load more
            </button>
          </div>
        )}
      </div>
    </div>
  );
}